import React from "react";
import styled, { keyframes } from "styled-components";


const CallChatNotification = ({ 
  icon,
  title,
  message,
  avatar,
  avatarFallback,
  avatarIcon,
  accentColor,
  indicatorColor,
  showIndicator = true,
  onClick,
  onClose,
}) => {
  const handleClick = () => {
    if (onClick) onClick();
    if (onClose) onClose();
  };

  const getAvatarFallback = () => {
    if (avatarFallback !== undefined) return avatarFallback;
    return title?.charAt(0)?.toUpperCase() || "?";
  };

  return (
    <NotificationContainer onClick={handleClick}>
      <NotificationContent $accentColor={accentColor}>
        <AvatarSection>
          {avatar ? (
            <Avatar src={avatar} alt={title} $accentColor={accentColor} />
          ) : avatarIcon ? (
            <AvatarPlaceholder $accentColor={accentColor} $isIcon={true}>
              {avatarIcon}
            </AvatarPlaceholder>
          ) : (
            <AvatarPlaceholder $accentColor={accentColor}>
              {getAvatarFallback()}
            </AvatarPlaceholder>
          )}
          {showIndicator && (
            <OnlineIndicator $indicatorColor={indicatorColor} />
          )}
        </AvatarSection>

        <ContentSection>
          <Header>
            <Title>{title}</Title>
            <IconSection $accentColor={accentColor}>
              {icon}
            </IconSection>
          </Header>
          <MessagePreview>{message}</MessagePreview>
        </ContentSection>

        <CloseButton onClick={(e) => {
          e.stopPropagation();
          if (onClose) onClose();
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </CloseButton>
      </NotificationContent>
    </NotificationContainer>
  );
};

export default CallChatNotification;

const slideIn = keyframes`
  from {
    transform: translateY(-20px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const NotificationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0 20px;
  cursor: pointer;
  animation: ${slideIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

const NotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 16px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 0 1px ${({ theme }) => theme.sidebar.borderColor} inset;
  backdrop-filter: blur(20px);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  min-width: 380px;
  max-width: 420px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}80,
      transparent
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s linear infinite;
  }

  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 
      0 12px 48px rgba(0, 0, 0, 0.5),
      0 0 40px ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}26,
      0 0 0 1px ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}4D inset;
    border-color: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor};
  }

  &:active {
    transform: translateY(-2px) scale(1.01);
  }
`;

const AvatarSection = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}66;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  ${NotificationContent}:hover & {
    border-color: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor};
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.4),
      0 0 20px ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}4D;
  }
`;

const AvatarPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor};
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $isIcon }) => $isIcon ? "24px" : "20px"};
  font-weight: 700;
  border: 2px solid ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}66;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  svg {
    width: ${({ $isIcon }) => $isIcon ? "24px" : "auto"};
    height: ${({ $isIcon }) => $isIcon ? "24px" : "auto"};
    fill: #FFFFFF;
  }

  ${NotificationContent}:hover & {
    border-color: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor};
    transform: scale(1.05);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.4),
      0 0 20px ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}4D;
  }
`;

const OnlineIndicator = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: ${({ theme, $indicatorColor }) => $indicatorColor || theme.valid_green};
  border: 2px solid ${({ theme }) => theme.callComponent.participantsListBackground};
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
  box-shadow: 0 0 8px ${({ theme, $indicatorColor }) => $indicatorColor || theme.valid_green}99;
`;

const ContentSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const MessagePreview = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconSection = styled.div`
  flex-shrink: 0;
  color: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}26;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}1A;

  svg {
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }

  ${NotificationContent}:hover & {
    background: ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}40;
    transform: rotate(12deg) scale(1.1);
    box-shadow: 0 2px 12px ${({ theme, $accentColor }) => $accentColor || theme.menu.selectedTextColor}4D;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border: none;
  background: ${({ theme }) => theme.chat.elementSecondaryColor}26;
  color: ${({ theme }) => theme.chat.elementSecondaryColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: 0;

  ${NotificationContent}:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.menu.deleteColor}33;
    color: ${({ theme }) => theme.menu.deleteColor};
    transform: rotate(90deg);
  }

  &:active {
    transform: rotate(90deg) scale(0.9);
  }
`;